interface LocalizedName {
  "zh-tw": string;
  "en-us": string;
}

interface DeliveryDistrict {
  name: LocalizedName;
  days: string;
  lineId: string;
}

interface DeliveryRegion {
  id: string;
  name: LocalizedName;
  districts: DeliveryDistrict[];
}

interface DeliveryData {
  regions: DeliveryRegion[];
  dayLabels: Record<string, Record<string, string>>;
  locale: keyof LocalizedName;
  qrCodes: Record<string, string>;
  lineeUrls: Record<string, string>;
}

document.addEventListener("DOMContentLoaded", () => {
  bindAsyncStylesheets();
  bindMobileMenu();
  bindBackToTop();
  bindQrPopovers(document);
  bindDeliveryWidgets();
});

function bindAsyncStylesheets(): void {
  document.querySelectorAll<HTMLLinkElement>("link[data-async-stylesheet]").forEach((link) => {
    link.media = "all";
  });
}

function bindMobileMenu(): void {
  const root = document.querySelector<HTMLElement>("[data-mobile-menu]");
  if (!root) return;

  const panel = root.querySelector<HTMLElement>("[data-menu-panel]");
  const openButton = root.querySelector<HTMLElement>("[data-menu-open]");
  const closeTargets = root.querySelectorAll<HTMLElement>("[data-menu-close]");
  let closeTimer: number | null = null;

  const setOpen = (open: boolean): void => {
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    if (panel) {
      if (open) {
        panel.hidden = false;
        window.requestAnimationFrame(() => {
          panel.dataset.state = "open";
        });
      } else {
        panel.dataset.state = "closed";
        closeTimer = window.setTimeout(() => {
          if (panel.dataset.state === "closed") panel.hidden = true;
        }, 300);
      }
    }
    document.documentElement.classList.toggle("overflow-hidden", open);
    openButton?.setAttribute("aria-expanded", open ? "true" : "false");
  };

  openButton?.addEventListener("click", () => setOpen(true));
  closeTargets.forEach((target) => target.addEventListener("click", () => setOpen(false)));
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape") setOpen(false);
  });
}

function bindBackToTop(): void {
  const button = document.querySelector<HTMLElement>("[data-back-to-top]");
  if (!button) return;

  const sync = (): void => {
    button.hidden = window.scrollY <= 400;
  };

  button.addEventListener("click", (event) => {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  });
  window.addEventListener("scroll", sync, { passive: true });
  sync();
}

function bindQrPopovers(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>("[data-qr-popover]").forEach((popover) => {
    const panel = popover.querySelector<HTMLElement>("[data-qr-panel]");
    if (!panel || popover.dataset.qrPopoverBound === "true") return;
    popover.dataset.qrPopoverBound = "true";

    const show = (): void => {
      panel.hidden = false;
    };
    const hide = (): void => {
      panel.hidden = true;
    };

    popover.addEventListener("mouseenter", show);
    popover.addEventListener("mouseleave", hide);
    popover.addEventListener("focusin", show);
    popover.addEventListener("focusout", hide);
  });
}

function bindDeliveryWidgets(): void {
  document.querySelectorAll<HTMLElement>("[data-delivery-widget]").forEach((root) => {
    const dataScript = root.querySelector<HTMLScriptElement>('[data-delivery-data]');
    if (!dataScript?.textContent || root.dataset.deliveryBound === "true") return;
    root.dataset.deliveryBound = "true";

    const data = JSON.parse(dataScript.textContent) as DeliveryData;
    const regionSelect = root.querySelector<HTMLSelectElement>("[data-delivery-region]");
    const districtSelect = root.querySelector<HTMLSelectElement>("[data-delivery-district]");
    const supported = root.querySelector<HTMLElement>("[data-delivery-supported]");
    const other = root.querySelector<HTMLElement>("[data-delivery-other]");
    const days = root.querySelector<HTMLElement>("[data-delivery-days]");
    const supportedMobile = root.querySelector<HTMLAnchorElement>("[data-delivery-supported-mobile]");
    const supportedDesktop = root.querySelector<HTMLAnchorElement>("[data-delivery-supported-desktop]");
    const supportedQr = root.querySelector<HTMLImageElement>("[data-delivery-supported-qr]");
    const otherMobile = root.querySelector<HTMLAnchorElement>("[data-delivery-other-mobile]");
    const otherDesktop = root.querySelector<HTMLAnchorElement>("[data-delivery-other-desktop]");
    const otherQr = root.querySelector<HTMLImageElement>("[data-delivery-other-qr]");

    const regionById = (): DeliveryRegion | undefined =>
      data.regions.find((region) => region.id === regionSelect?.value);

    const lineUrl = (lineId: string, text: string): string =>
      `https://line.me/R/oaMessage/%40${lineId}/?${encodeURIComponent(text)}`;

    const otherText = root.dataset.otherText || "";
    const otherUrl = lineUrl("679zyygj", otherText);
    if (otherMobile) otherMobile.href = otherUrl;
    if (otherDesktop) otherDesktop.href = data.lineeUrls["679zyygj"] || otherUrl;
    if (otherQr) otherQr.src = data.qrCodes.other || "";

    const renderDistrictOptions = (): void => {
      if (!districtSelect) return;
      const region = regionById();
      districtSelect.replaceChildren(new Option(root.dataset.selectDistrict || "", ""));
      if (region) {
        region.districts.forEach((district, index) => {
          districtSelect.append(new Option(district.name[data.locale], String(index)));
        });
      }
      districtSelect.append(new Option(root.dataset.otherLabel || "Other", "other"));
      districtSelect.disabled = !region || regionSelect?.value === "other";
      districtSelect.classList.toggle("bg-white", Boolean(region));
      districtSelect.classList.toggle("text-near-black", Boolean(region));
      districtSelect.classList.toggle("bg-gray-100", !region);
      districtSelect.classList.toggle("text-secondary", !region);
    };

    const render = (): void => {
      const region = regionById();
      const districtIndex = districtSelect?.value || "";
      const district = region && districtIndex !== "" && districtIndex !== "other"
        ? region.districts[Number(districtIndex)]
        : undefined;
      const isOther = regionSelect?.value === "other" || districtIndex === "other";

      if (supported) supported.hidden = !district || isOther;
      if (other) other.hidden = !isOther;

      if (!district || !region || isOther) return;

      if (days) {
        const labels = data.dayLabels[data.locale] || {};
        days.textContent = district.days
          .split("")
          .map((day) => labels[day] || day)
          .join("･");
      }

      const template = root.dataset.orderTemplate || "";
      const message = template
        .replace("{region}", region.name[data.locale])
        .replace("{district}", district.name[data.locale]);
      const url = lineUrl(district.lineId, message);
      const fallback = data.lineeUrls[district.lineId] || url;
      const qrKey = `${region.id}_${districtIndex}`;

      if (supportedMobile) supportedMobile.href = url;
      if (supportedDesktop) supportedDesktop.href = fallback;
      if (supportedQr) supportedQr.src = data.qrCodes[qrKey] || "";
    };

    regionSelect?.addEventListener("change", () => {
      renderDistrictOptions();
      render();
    });
    districtSelect?.addEventListener("change", render);
    renderDistrictOptions();
    render();
  });
}
